import { FC } from 'react';
import { Button, IconRefresh } from '@screentone/core';

import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import {
  ModuleContainer,
  MultiZoneModuleContainer,
  SectionContainer,
  UiModuleType,
  UiTitleTreatmentType
} from 'data/generated/graphql';
import { useIsAdmin } from 'hooks/roles';

interface ResetPageTypeSettingsProps {
  isReadOnly?: boolean;
}

export const ResetPageTypeSettings: FC<ResetPageTypeSettingsProps> = ({ isReadOnly = false }) => {
  const isAdmin = useIsAdmin();
  const { modifyAllByType } = useDataModelContext<SectionContainer>();
  const { getTreatmentTypeSetting } = usePageTypeSettingsContext();

  // Only render this button for admins
  if (!isAdmin) return null;

  const handleResetPageTypeSettings = () => {
    modifyAllByType<ModuleContainer | MultiZoneModuleContainer>(EntityType.Module, (module) => {
      if (!module.attributes.pageModule) {
        console.warn('Could not reset page type setting for module without pageModule');
        return;
      }

      const treatmentTypeSetting = getTreatmentTypeSetting(module.attributes.pageModule);
      if (!treatmentTypeSetting) {
        console.warn(
          'Could not reset page type setting for module. Did not treatment type settings for module',
          module.attributes.pageModule
        );
        return;
      }
      if (!treatmentTypeSetting.module) {
        console.warn(
          'Could not reset page type setting for module. Did not find default page module settings for module',
          module.attributes.pageModule
        );
        return;
      }

      // structure pages have uiModuleFields: null until hydrated at render time; nothing to reset.
      if (!module.attributes.pageModule.uiModuleFields) return;

      // Convert basic module to title module if needed
      if (
        module.attributes.pageModule.uiModuleType === UiModuleType.UiBasicModuleType &&
        !!module.attributes.pageModule.uiModuleFields.basicModule
      ) {
        /* eslint-disable no-param-reassign */
        module.attributes.pageModule.uiModuleType = UiModuleType.UiTitleModuleType;
        module.attributes.pageModule.uiModuleFields.titleModule = {
          ...module.attributes.pageModule.uiModuleFields.basicModule,
          treatmentType: module.attributes.pageModule.uiModuleFields.basicModule.treatmentType as UiTitleTreatmentType,
          allowedTreatmentTypes: module.attributes.pageModule.uiModuleFields.basicModule
            .allowedTreatmentTypes as UiTitleTreatmentType[],
          __typename: 'UiTitleModule'
        };
        module.attributes.pageModule.uiModuleFields.basicModule = null;
        /* eslint-enable no-param-reassign */
      }

      // Preserve title and subtitle values if they exist
      const isTitleModule =
        typeof module.attributes.pageModule.uiModuleFields.titleModule === 'object' &&
        module.attributes.pageModule.uiModuleFields.titleModule !== null;
      const isRankedModule =
        typeof module.attributes.pageModule.uiModuleFields.rankedModule === 'object' &&
        module.attributes.pageModule.uiModuleFields.rankedModule !== null;
      const moduleKey = isTitleModule ? 'titleModule' : isRankedModule ? 'rankedModule' : 'titleModule';

      let prevTitle = '';
      let prevTitleUrl = '';
      let prevSubtitle = '';
      let prevSubtitleUrl = '';
      if (isTitleModule || isRankedModule) {
        prevTitle = module.attributes.pageModule.uiModuleFields[moduleKey]!.title ?? '';
        if (isTitleModule) {
          prevTitleUrl = module.attributes.pageModule.uiModuleFields.titleModule!.titleUrl ?? '';
          prevSubtitle = module.attributes.pageModule.uiModuleFields.titleModule!.subtitle ?? '';
          prevSubtitleUrl = module.attributes.pageModule.uiModuleFields.titleModule!.subtitleUrl ?? '';
        }
      }

      /* eslint-disable no-param-reassign */
      module.attributes.pageModule = treatmentTypeSetting.module;
      module.subtype = treatmentTypeSetting.subtype;
      module.attributes.brand = treatmentTypeSetting.brand;

      // uiModuleFields may be null on the new module set above (treatmentTypeSetting.module)
      if ((isTitleModule || isRankedModule) && module.attributes.pageModule.uiModuleFields) {
        if (prevTitle) {
          module.attributes.pageModule.uiModuleFields[moduleKey]!.title = prevTitle;
        }
        if (isTitleModule) {
          if (prevTitleUrl) {
            module.attributes.pageModule.uiModuleFields.titleModule!.titleUrl = prevTitleUrl;
          }
          if (prevSubtitle) {
            module.attributes.pageModule.uiModuleFields.titleModule!.subtitle = prevSubtitle;
          }
          if (prevSubtitleUrl) {
            module.attributes.pageModule.uiModuleFields.titleModule!.subtitleUrl = prevSubtitleUrl;
          }
        }
      }
      /* eslint-enable no-param-reassign */
    });
  };

  return (
    <Button
      tertiary
      icon={IconRefresh}
      onClick={handleResetPageTypeSettings}
      data-testid="settings-reset-page-type-settings-button"
      disabled={isReadOnly}
    >
      Reset page type settings
    </Button>
  );
};
